import { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from './useAuth.jsx';
import { useSchool } from '../../../contexts/SchoolContext';
import { firestoreService } from '../../../firestoreService';

const RoleSwitcherContext = createContext();

export const useRoleSwitcher = () => {
  const context = useContext(RoleSwitcherContext);
  if (!context) {
    throw new Error('useRoleSwitcher must be used within a RoleSwitcherProvider');
  }
  return context;
};

export const RoleSwitcherProvider = ({ children }) => {
  const { user } = useAuth();
  const { permissions: schoolPermissions, loading: schoolLoading } = useSchool();
  const [userPermissions, setUserPermissions] = useState(null);

  // Load last-used role from sessionStorage synchronously (instant, no flash)
  const [currentRole, setCurrentRole] = useState(() => {
    try {
      const lastRole = sessionStorage.getItem('swan_last_role');
      return lastRole || null;
    } catch {
      return null;
    }
  });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Helper to update role and persist to sessionStorage
  const updateCurrentRole = (role) => {
    setCurrentRole(role);
    try {
      sessionStorage.setItem('swan_last_role', role);
    } catch (e) {
      // Fail silently
    }
  };

  // Fetch user permissions - prioritize SchoolContext, fallback to Firestore
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setHasInitialized(false);
      return;
    }

    // Only fetch permissions once per user session
    if (hasInitialized && userPermissions) {
      return;
    }

    // Don't proceed if user.uid isn't available yet
    if (!user.uid) {
      console.log('[RoleSwitcher] Waiting for user.uid');
      return;
    }

    // Wait for SchoolContext to finish loading
    if (schoolLoading) {
      console.log('[RoleSwitcher] Waiting for SchoolContext to load');
      return;
    }

    const setupPermissions = async () => {
      try {
        // Handle development bypass user
        if (user.uid === 'dev-user-123') {
          const mockUserDoc = {
            dev: true,
            admin: true,
            staff: false
          };
          setUserPermissions(mockUserDoc);

          const roles = ['teacher', 'admin', 'parent'];
          setAvailableRoles(roles);

          const savedRole = localStorage.getItem(`rolePreference_${user.uid}`);
          if (savedRole && roles.includes(savedRole)) {
            updateCurrentRole(savedRole);
          } else {
            updateCurrentRole('dev');
          }
          setLoading(false);
          setHasInitialized(true);
          return;
        }

        // PRIORITY 1: Use permissions from SchoolContext (already loaded by getUserInitData)
        if (schoolPermissions) {
          setUserPermissions(schoolPermissions);

          // Cache for offline use
          try {
            localStorage.setItem(`userPermissions_${user.uid}`, JSON.stringify(schoolPermissions));
          } catch (e) {
            // Ignore cache errors
          }

          // Set up roles from SchoolContext permissions - accessRole-based calculation
          const roles = [];

          // Derive available roles from school-scoped accessRole (not legacy boolean flags)
          const accessRole = schoolPermissions.accessRole;
          if (accessRole === 'staff' || accessRole === 'admin' || schoolPermissions.userType === 'teacher') roles.push('teacher');
          if (accessRole === 'admin') roles.push('admin');
          if (schoolPermissions.parent || schoolPermissions.userType === 'parent') roles.push('parent');
          if (schoolPermissions.dev) roles.push('dev');

          setAvailableRoles(roles);

          // If current role is already set and still valid, keep it.
          // This prevents real-time permission updates from overriding
          // the user's active role (e.g. flipping parent → teacher).
          if (currentRole && roles.includes(currentRole)) {
            // Current role still valid — don't override
          } else {
            // No current role or it's invalid — restore saved preference or pick default
            const savedRole = localStorage.getItem(`rolePreference_${user.uid}`);
            if (savedRole && roles.includes(savedRole)) {
              updateCurrentRole(savedRole);
            } else {
              // Default role priority: dev > admin > parent > teacher
              if (roles.includes('dev')) {
                updateCurrentRole('dev');
              } else if (roles.includes('admin')) {
                updateCurrentRole('admin');
              } else if (roles.includes('parent')) {
                updateCurrentRole('parent');
              } else if (roles.includes('teacher')) {
                updateCurrentRole('teacher');
              } else if (roles.length > 0) {
                updateCurrentRole(roles[0]);
              }
            }
          }

          setLoading(false);
          return;
        }

        // PRIORITY 2: Try localStorage cache
        const cacheKey = `userPermissions_${user.uid}`;
        const cachedPermissions = localStorage.getItem(cacheKey);

        if (cachedPermissions) {
          try {
            const userDoc = JSON.parse(cachedPermissions);
            console.log('[RoleSwitcher] 📦 Using cached permissions');
            setUserPermissions(userDoc);

            // Set up roles from cached data - accessRole-based calculation
            const roles = [];

            // Derive available roles from school-scoped accessRole (not legacy boolean flags)
            const accessRoleCached = userDoc.accessRole;
            if (accessRoleCached === 'staff' || accessRoleCached === 'admin' || userDoc.userType === 'teacher') roles.push('teacher');
            if (accessRoleCached === 'admin') roles.push('admin');
            if (userDoc.parent || userDoc.userType === 'parent') roles.push('parent');
            if (userDoc.dev) roles.push('dev');

            setAvailableRoles(roles);

            if (currentRole && roles.includes(currentRole)) {
              // Current role still valid — don't override
            } else {
              const savedRole = localStorage.getItem(`rolePreference_${user.uid}`);
              if (savedRole && roles.includes(savedRole)) {
                updateCurrentRole(savedRole);
              } else {
                if (roles.includes('dev')) {
                  updateCurrentRole('dev');
                } else if (roles.includes('admin')) {
                  updateCurrentRole('admin');
                } else if (roles.includes('parent')) {
                  updateCurrentRole('parent');
                } else if (roles.includes('teacher')) {
                  updateCurrentRole('teacher');
                } else if (roles.length > 0) {
                  updateCurrentRole(roles[0]);
                }
              }
            }

            setLoading(false);
            return;
          } catch (e) {
            console.error('[RoleSwitcher] Failed to parse cached permissions:', e);
          }
        }

        // FALLBACK: Fetch from Firestore if not available from SchoolContext or cache
        console.log('[RoleSwitcher] ⚠️ Permissions not in SchoolContext or cache, fetching from Firestore...');
        const userDoc = await firestoreService.getById('users', user.uid);

        if (userDoc) {
          localStorage.setItem(`userPermissions_${user.uid}`, JSON.stringify(userDoc));
          setUserPermissions(userDoc);

          const roles = [];
          const accessRoleFirestore = userDoc.accessRole;
          if (accessRoleFirestore === 'staff' || accessRoleFirestore === 'admin' || userDoc.userType === 'teacher') roles.push('teacher');
          if (accessRoleFirestore === 'admin') roles.push('admin');
          if (userDoc.parent || userDoc.userType === 'parent') roles.push('parent');
          if (userDoc.dev) roles.push('dev');

          setAvailableRoles(roles);

          if (currentRole && roles.includes(currentRole)) {
            // Current role still valid — don't override
          } else {
            const savedRole = localStorage.getItem(`rolePreference_${user.uid}`);
            if (savedRole && roles.includes(savedRole)) {
              updateCurrentRole(savedRole);
            } else {
              if (roles.includes('dev')) {
                updateCurrentRole('dev');
              } else if (roles.includes('admin')) {
                updateCurrentRole('admin');
              } else if (roles.includes('parent')) {
                updateCurrentRole('parent');
              } else if (roles.includes('teacher')) {
                updateCurrentRole('teacher');
              } else if (roles.length > 0) {
                updateCurrentRole(roles[0]);
              }
            }
          }
        } else {
          console.warn('[RoleSwitcher] No user document found');
        }
      } catch (error) {
        console.error('[RoleSwitcher] Error setting up permissions:', error);
      } finally {
        setLoading(false);
        setHasInitialized(true);
      }
    };

    setupPermissions();
  }, [user, schoolPermissions, schoolLoading]);

  // React to permission changes from SchoolContext (real-time updates)
  useEffect(() => {
    if (!schoolPermissions || !user?.uid) return;

    // Update userPermissions with latest data from SchoolContext
    setUserPermissions(schoolPermissions);

    // Recalculate available roles when permissions change — accessRole-based
    const roles = [];

    // Derive available roles from school-scoped accessRole (not legacy boolean flags)
    const accessRoleRT = schoolPermissions.accessRole;
    if (accessRoleRT === 'staff' || accessRoleRT === 'admin' || schoolPermissions.userType === 'teacher') roles.push('teacher');
    if (accessRoleRT === 'admin') roles.push('admin');
    if (schoolPermissions.parent || schoolPermissions.userType === 'parent') roles.push('parent');
    if (schoolPermissions.dev) roles.push('dev');

    // Check if roles actually changed
    const rolesChanged = JSON.stringify(availableRoles.sort()) !== JSON.stringify(roles.sort());

    if (rolesChanged) {
      console.log('[RoleSwitcher] 🔄 Permissions changed, updating available roles', {
        old: availableRoles,
        new: roles
      });

      setAvailableRoles(roles);

      // If current role is no longer available, switch to default
      if (!roles.includes(currentRole)) {
        console.log('[RoleSwitcher] ⚠️ Current role no longer available, switching to default');
        // Default role priority: dev > admin > parent > teacher
        // Only set a role if it's in the available roles array
        if (roles.includes('dev')) {
          updateCurrentRole('dev');
        } else if (roles.includes('admin')) {
          updateCurrentRole('admin');
        } else if (roles.includes('parent')) {
          updateCurrentRole('parent');
        } else if (roles.includes('teacher')) {
          updateCurrentRole('teacher');
        } else if (roles.length > 0) {
          updateCurrentRole(roles[0]); // fallback to first available role
        }
      }

      // Update cached permissions
      try {
        localStorage.setItem(`userPermissions_${user.uid}`, JSON.stringify(schoolPermissions));
      } catch (e) {
        console.warn('[RoleSwitcher] Failed to cache updated permissions:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolPermissions]);

  const switchRole = (newRole) => {
    if (!availableRoles.includes(newRole)) {
      console.warn(`Role ${newRole} not available for current user`);
      return false;
    }

    console.log('[RoleSwitcher] 🔄 Switching role:', { from: currentRole, to: newRole });

    // Don't trigger loading state - role switch is just a view change, not a data reload
    // User identity (photo, email, displayName) stays the same across role switches

    // Clear all sessionStorage caches to prevent data leakage between roles
    try {
      const keys = Object.keys(sessionStorage);
      let clearedCount = 0;

      keys.forEach(key => {
        // Keep auth-related data and last role, clear everything else
        if (!key.includes('firebase:authUser') && key !== 'swan_last_role') {
          sessionStorage.removeItem(key);
          console.log('[RoleSwitcher] 🗑️ Cleared cache:', key);
          clearedCount++;
        }
      });

      console.log(`[RoleSwitcher] ✅ Cleared ${clearedCount} cache entries`);
    } catch (e) {
      console.error('[RoleSwitcher] ❌ Cache clear failed:', e);
    }

    // Update role (RoleBasedRoute will handle any necessary redirects)
    updateCurrentRole(newRole);

    // Save preference for users who have multiple roles available
    if (availableRoles.length > 1 && user) {
      localStorage.setItem(`rolePreference_${user.uid}`, newRole);
    }

    return true;
  };

  const canSwitchRoles = () => {
    // Allow role switching for any user with 2+ roles
    return availableRoles.length > 1;
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      teacher: 'Teacher',
      admin: 'Administrator', 
      parent: 'Parent',
      dev: 'Developer'
    };
    return roleNames[role] || role;
  };

  const getRolePermissions = (role) => {
    const permissions = {
      teacher: {
        canManageClasses: true,
        canManageStudents: true,
        canViewReports: true,
        canManageSeating: true,
        canAccessAdminPanel: false,
        canManageUsers: false
      },
      admin: {
        canManageClasses: true,
        canManageStudents: true,
        canViewReports: true,
        canManageSeating: true,
        canAccessAdminPanel: true,
        canManageUsers: true
      },
      parent: {
        canManageClasses: false,
        canManageStudents: false,
        canViewReports: false,
        canManageSeating: false,
        canAccessAdminPanel: false,
        canManageUsers: false,
        canViewOwnChildren: true
      },
      dev: {
        canManageClasses: true,
        canManageStudents: true,
        canViewReports: true,
        canManageSeating: true,
        canAccessAdminPanel: true,
        canManageUsers: true,
        canSwitchRoles: true
      }
    };
    return permissions[role] || permissions.teacher;
  };

  const value = {
    userPermissions,
    currentRole,
    availableRoles,
    loading,
    switchRole,
    canSwitchRoles,
    getRoleDisplayName,
    getRolePermissions,
    // Helper to get current role permissions
    currentPermissions: getRolePermissions(currentRole)
  };

  return (
    <RoleSwitcherContext.Provider value={value}>
      {children}
    </RoleSwitcherContext.Provider>
  );
};
